/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
// BOGOS offers on the product page: offer data and bundle add-to-cart go through our cart API,
// which talks to the BOGOS partner API server-side
class BogosOffers extends Core {
	constructor(container) {
		super();
		this.container = container;
		this.productId = container.dataset.productId;
		this.discountTemplate = document.getElementById("bogos-discount-template");
		this.tierTemplate = document.getElementById("bogos-discount-tier-template");
		this.productTemplate = document.getElementById("bogos-discount-product-template");
		this.bundleTemplate = document.getElementById("bogos-bundle-template");
		this.stepTemplate = document.getElementById("bogos-bundle-step-template");
		this.optionTemplate = document.getElementById("bogos-bundle-option-template");
	}

	async init() {
		const offers = await this.fetchOffers();
		if (!offers) return;

		offers.discounts.forEach((discount) => this.renderDiscount(discount));
		offers.bundles.forEach((bundle) => this.renderBundle(bundle));

		// Discount tiers follow what's in the cart
		["cart:addItems", "cart:removeItems"].forEach((event) => {
			window.addEventListener(event, () => this.refreshDiscountTiers());
		});
	}

	async fetchOffers() {
		const cartParam = window.cart.cartId ? `&cartId=${encodeURIComponent(window.cart.cartId)}` : "";
		const request = await this.asyncRequest({
			url: `${this.apiUrl}/cart/bogos-offers?productId=${this.productId}&site=${this.siteId}${cartParam}`,
		});
		if (!request || request.status !== 200) return null;

		const { discounts = [], bundles = [] } = JSON.parse(request.response);
		return { discounts, bundles };
	}

	async refreshDiscountTiers() {
		const offers = await this.fetchOffers();
		offers?.discounts.forEach((discount) => {
			const section = this.container.querySelector(`[data-bogos-discount-id="${discount.id}"]`);
			if (section) this.updateTiers(section, discount);
		});
	}

	// Cheapest-item offer: countdown, tiers ticked as the cart qualifies, and products to pick from
	renderDiscount(discount) {
		const node = this.discountTemplate.content.cloneNode(true);
		const section = node.querySelector("section");
		section.dataset.bogosDiscountId = discount.id;
		section.querySelector("[data-bogos-discount-title]").innerText = discount.subTitle || discount.title;
		section.querySelector("[data-bogos-discount-description]").innerText = discount.description || "";

		const tiersList = section.querySelector("[data-bogos-discount-tiers]");
		discount.tiers.forEach(({ title, quantity }) => {
			const tierNode = this.tierTemplate.content.cloneNode(true);
			tierNode.querySelector("li").dataset.quantity = quantity;
			tierNode.querySelector("[data-bogos-tier-title]").innerText = title;
			tiersList.appendChild(tierNode);
		});
		this.updateTiers(section, discount);

		const collectionLink = section.querySelector("[data-bogos-discount-collection]");
		if (discount.collection) collectionLink.href = `/collections/${discount.collection.handle}/`;
		else collectionLink.remove();

		const productsList = section.querySelector("[data-bogos-discount-products]");
		discount.products.forEach((product) => productsList.appendChild(this.renderDiscountProduct(product)));
		if (!discount.products.length) productsList.previousElementSibling.remove();

		if (discount.endTime) this.startCountdown(section.querySelector("[data-bogos-countdown]"), discount.endTime);
		this.container.appendChild(node);
	}

	updateTiers(section, { eligibleQuantity = 0 }) {
		section.querySelectorAll("[data-bogos-discount-tiers] li").forEach((tier) => {
			tier.toggleAttribute("data-reached", eligibleQuantity >= Number(tier.dataset.quantity));
		});
	}

	renderDiscountProduct(product) {
		const node = this.productTemplate.content.cloneNode(true);
		const link = `/products/${product.handle}/`;
		node.querySelectorAll("[data-bogos-product-link]").forEach((anchor) => anchor.setAttribute("href", link));
		node.querySelector("[data-bogos-product-title]").innerText = product.title;

		const image = node.querySelector("[data-bogos-product-image]");
		if (product.image) {
			image.setAttribute("src", product.image.url);
			image.setAttribute("alt", product.image.altText || product.title);
		}

		const price = node.querySelector("[data-bogos-product-price]");
		const variantTitle = node.querySelector("[data-bogos-product-variant-title]");
		const select = node.querySelector("[data-bogos-product-variants]");
		const showVariant = (variant) => {
			price.innerText = Core.formatCurrency(variant.price);
			variantTitle.innerText = variant.title === "Default Title" ? "" : variant.title;
		};

		if (product.variants.length > 1) {
			product.variants.forEach((variant) => select.add(new Option(variant.title, variant.id)));
			select.hidden = false;
			variantTitle.hidden = true;
			select.addEventListener("change", () => showVariant(product.variants[select.selectedIndex]));
		}
		showVariant(product.variants[0]);

		node.querySelector("[data-bogos-product-add]").addEventListener("click", (e) =>
			this.addDiscountProduct(e, product.variants[select.selectedIndex]?.id ?? product.variants[0].id)
		);
		return node;
	}

	async addDiscountProduct(e, merchandiseId) {
		const button = e.currentTarget;
		button.disabled = true;

		const items = [{ merchandiseId, quantity: 1 }];
		const previousCart = structuredClone(window.cart);
		try {
			const cart = await this.addItemsCart(e, {
				site: this.siteId,
				...(window.cart.cartId && { cartId: window.cart.cartId }),
				items,
			});
			if (cart?.lines) this.showMessage(cart, previousCart, items);
		} catch (error) {
			this.messages.addMessage(["Sorry, we couldn't add this item to your bag"], "error");
		}
		button.disabled = false;
	}

	startCountdown(countdown, endTime) {
		const parts = Array.from(countdown.querySelectorAll("[data-bogos-countdown-part]"));
		const tick = () => {
			const remaining = Date.parse(endTime) - Date.now();
			countdown.hidden = remaining <= 0;
			if (remaining <= 0) return false;

			const seconds = Math.floor(remaining / 1000);
			const values = {
				days: Math.floor(seconds / 86400),
				hours: Math.floor(seconds / 3600) % 24,
				minutes: Math.floor(seconds / 60) % 60,
				seconds: seconds % 60,
			};
			parts.forEach((part) => {
				part.innerText = String(values[part.dataset.bogosCountdownPart]).padStart(2, "0");
			});
			return true;
		};

		if (tick()) {
			const timer = setInterval(() => {
				if (!tick()) clearInterval(timer);
			}, 1000);
		}
	}

	renderBundle(bundle) {
		const node = this.bundleTemplate.content.cloneNode(true);
		const section = node.querySelector("section");
		section.querySelector("[data-bogos-bundle-title]").innerText = bundle.title;
		section.querySelector("[data-bogos-bundle-description]").innerText = bundle.description || "";

		const products = bundle.products.find((rule) => rule.type === "variants_ids")?.value ?? [];
		const steps = bundle.products.find((rule) => rule.type === "config")?.value?.mix_items ?? [];
		const tiers = [...bundle.discount].sort((a, b) => a.quantity - b.quantity);

		// step id -> selected options, in the order they were picked
		const selection = new Map(steps.map((step) => [step.id, []]));
		const stepsList = section.querySelector("[data-bogos-bundle-steps]");
		const addButton = section.querySelector("[data-bogos-bundle-add]");

		const update = () => {
			const selected = [...selection.values()].flat();
			const subtotal = selected.reduce((total, { variant }) => total + Number(variant.price), 0);
			const tier = tiers.filter(({ quantity }) => selected.length >= quantity).pop();
			const total = tier ? this.applyTier(subtotal, tier.discount) : subtotal;
			const stepsComplete = steps.every((step) => selection.get(step.id).length >= (step.min_quantity ?? 0));

			section.querySelector("[data-bogos-bundle-tier]").innerText = (tier ?? tiers[0])?.label ?? "";
			section.querySelector("[data-bogos-bundle-original]").innerText =
				total < subtotal ? Core.formatCurrency(subtotal) : "";
			section.querySelector("[data-bogos-bundle-total]").innerText = Core.formatCurrency(total);
			addButton.disabled = !(stepsComplete && tier);
			stepsList.querySelectorAll("[data-bogos-option]").forEach((button) => {
				const isSelected = selected.some(({ variant }) => String(variant.id) === button.dataset.variantId);
				button.setAttribute("aria-pressed", `${isSelected}`);
			});
		};

		steps.forEach((step, index) => {
			const options = products
				.filter((product) => step.product_ids.includes(product.id))
				.flatMap((product) =>
					product.variants
						.filter((variant) => variant.mix_item_ids?.includes(step.id))
						.map((variant) => ({ product, variant }))
				);
			stepsList.appendChild(this.renderStep(step, index, options, selection, update));

			// The product being viewed starts selected in its own step
			const current = options.find(({ product }) => String(product.id) === this.productId);
			if (current) selection.get(step.id).push(current);
		});

		addButton.addEventListener("click", (e) => this.addBundle(e, bundle, [...selection.values()].flat()));
		update();
		this.container.appendChild(node);
	}

	renderStep(step, index, options, selection, onChange) {
		const node = this.stepTemplate.content.cloneNode(true);
		const { min_quantity: min = 1, max_quantity: max = min } = step;
		const count = min === max ? min : `${min} to ${max}`;
		node.querySelector("[data-bogos-step-title]").innerText = `Step ${index + 1}: choose ${count}`;

		const list = node.querySelector("[data-bogos-step-options]");
		options.forEach((option) => {
			const { product, variant } = option;
			const optionNode = this.optionTemplate.content.cloneNode(true);
			const button = optionNode.querySelector("[data-bogos-option]");
			button.dataset.variantId = variant.id;

			const image = optionNode.querySelector("[data-bogos-option-image]");
			image.setAttribute("src", variant.thumbnail || product.thumbnail);
			image.setAttribute("alt", product.title);
			optionNode.querySelector("[data-bogos-option-title]").innerText = product.title;
			if (variant.title !== "Default Title") {
				optionNode.querySelector("[data-bogos-option-variant]").innerText = variant.title;
			}
			optionNode.querySelector("[data-bogos-option-price]").innerText = Core.formatCurrency(variant.price);

			// Picking past the step's maximum replaces the oldest pick
			button.addEventListener("click", () => {
				const picked = selection.get(step.id);
				const position = picked.findIndex((pick) => pick.variant.id === variant.id);
				if (position >= 0) picked.splice(position, 1);
				else {
					picked.push(option);
					if (picked.length > max) picked.shift();
				}
				onChange();
			});
			list.appendChild(optionNode);
		});

		return node;
	}

	applyTier(subtotal, { type, value }) {
		if (type === "percentage") return subtotal * (1 - value / 100);
		return Math.max(0, subtotal - value);
	}

	async addBundle(e, bundle, selected) {
		const button = e.currentTarget;
		button.disabled = true;

		const items = selected.map(({ variant }) => ({
			merchandiseId: `gid://shopify/ProductVariant/${variant.id}`,
			quantity: 1,
		}));
		const previousCart = structuredClone(window.cart);

		const request = await this.asyncRequest({
			url: `${this.apiUrl}/cart/add-bundle`,
			method: "POST",
			headers: { "Content-Type": "application/json;charset=UTF-8" },
			data: JSON.stringify({
				site: this.siteId,
				...(window.cart.cartId && { cartId: window.cart.cartId }),
				offerId: bundle.id,
				productId: this.productId,
				items,
			}),
		});
		button.disabled = false;

		if (!request || request.status !== 200) {
			this.messages.addMessage(["Sorry, we couldn't add the bundle to your bag"], "error");
			return;
		}

		const { cart, userErrors = [], warnings = [], bogos } = JSON.parse(request.response);
		if (userErrors.length > 0) this.messages.addMessage([userErrors[0].message], "error");
		if (warnings.length > 0) this.messages.addMessage([warnings[0].message], "warning");
		if (!cart) return;

		this.setCartStatus(cart);
		this.showBogosMessages(bogos);
		window.dispatchEvent(
			new CustomEvent("cart:addItems", {
				detail: { items: this.cartAddedItems(cart, items), eventLabel: "Bundle" },
			})
		);
		this.showMessage(cart, previousCart, items);
	}
}

window.addEventListener("DOMContentLoaded", () => {
	const container = document.querySelector("[data-bogos-offers]");
	if (!container) return;

	const bogosOffers = new BogosOffers(container);
	bogosOffers.init();
});
